import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DiscoveredKit, KitContext } from '../shared/discoverKits';

interface KitsProps {
    kits: DiscoveredKit[];
}

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
    ready: {
        bg: 'var(--nj-core-color-green-100)',
        fg: 'var(--nj-core-color-green-700)',
    },
    partial: {
        bg: 'var(--nj-core-color-orange-100)',
        fg: 'var(--nj-core-color-orange-700)',
    },
    planned: {
        bg: 'var(--nj-core-color-grey-200)',
        fg: 'var(--nj-core-color-grey-700)',
    },
};

const CONTEXT_LABELS: Record<KitContext, string> = {
    size2mix: 'Size2Mix',
    urbanmap: 'Urban Map',
};

type ContextFilter = 'all' | KitContext;

export default function Kits({ kits }: KitsProps) {
    const [filter, setFilter] = useState<ContextFilter>('all');
    const filteredKits =
        filter === 'all' ? kits : kits.filter((k) => k.manifest.context === filter);

    return (
        <div
            style={{
                minHeight: '100vh',
                background:
                    'var(--nj-semantic-color-background-neutral-secondary-default)',
                fontFamily: 'var(--nj-core-font-family-sans)',
            }}
        >
            <header
                style={{
                    height: '56px',
                    background:
                        'var(--nj-semantic-color-background-neutral-primary-default)',
                    borderBottom: '1px solid var(--nj-core-color-grey-300)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 24px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Link
                        to="/"
                        style={{
                            fontSize: 13,
                            color: 'var(--nj-semantic-color-text-secondary-default)',
                            textDecoration: 'none',
                        }}
                    >
                        ← Prototypes
                    </Link>
                    <h1
                        style={{
                            margin: 0,
                            fontSize: 16,
                            fontWeight: 600,
                            color: 'var(--nj-semantic-color-text-primary-default)',
                        }}
                    >
                        Kits Nemo
                    </h1>
                </div>
                <span
                    style={{
                        fontSize: 12,
                        color: 'var(--nj-semantic-color-text-secondary-default)',
                    }}
                >
                    {filteredKits.length} kit{filteredKits.length > 1 ? 's' : ''}
                </span>
            </header>

            <main
                style={{
                    maxWidth: 880,
                    margin: '32px auto',
                    padding: '0 24px',
                }}
            >
                <p
                    style={{
                        fontSize: 13,
                        color: 'var(--nj-semantic-color-text-secondary-default)',
                        marginTop: 0,
                        marginBottom: 24,
                    }}
                >
                    Un kit regroupe les ressources nécessaires pour prototyper
                    rapidement une <strong>area de Nemo</strong> : pointeurs vers
                    le code réel, captures d&apos;écran, vues de référence,
                    composants partagés à utiliser.
                </p>

                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {(['all', 'size2mix', 'urbanmap'] as ContextFilter[]).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '6px 14px',
                                borderRadius: 16,
                                border:
                                    filter === f
                                        ? '1px solid var(--nj-core-color-brand-500)'
                                        : '1px solid var(--nj-core-color-grey-200)',
                                background:
                                    filter === f
                                        ? 'var(--nj-core-color-brand-100)'
                                        : 'white',
                                color:
                                    filter === f
                                        ? 'var(--nj-core-color-brand-700)'
                                        : 'var(--nj-semantic-color-text-secondary-default)',
                                cursor: 'pointer',
                            }}
                        >
                            {f === 'all' ? 'Tous' : CONTEXT_LABELS[f]}
                        </button>
                    ))}
                </div>

                {filteredKits.length === 0 ? (
                    <p
                        style={{
                            color: 'var(--nj-semantic-color-text-secondary-default)',
                        }}
                    >
                        Aucun kit pour l&apos;instant.
                    </p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {filteredKits.map(({ slug, manifest }) => {
                            const color =
                                STATUS_COLORS[manifest.status] ??
                                STATUS_COLORS.planned;
                            return (
                                <li key={slug} style={{ marginBottom: 12 }}>
                                    <Link
                                        to={`/kits/${slug}`}
                                        style={{
                                            display: 'block',
                                            background: 'white',
                                            borderRadius: 8,
                                            padding: '16px 20px',
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            border: '1px solid var(--nj-core-color-grey-200)',
                                            transition:
                                                'border-color 0.15s, box-shadow 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor =
                                                'var(--nj-core-color-brand-500)';
                                            e.currentTarget.style.boxShadow =
                                                '0 2px 6px rgba(0,0,0,0.06)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor =
                                                'var(--nj-core-color-grey-200)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 16,
                                            }}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: 15,
                                                    }}
                                                >
                                                    {manifest.name}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 13,
                                                        color: 'var(--nj-semantic-color-text-secondary-default)',
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    {manifest.description}
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 6,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        background:
                                                            'var(--nj-core-color-grey-100)',
                                                        color: 'var(--nj-semantic-color-text-secondary-default)',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        padding: '3px 8px',
                                                        borderRadius: 4,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {CONTEXT_LABELS[manifest.context]}
                                                </span>
                                                <span
                                                    style={{
                                                        background: color.bg,
                                                        color: color.fg,
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        padding: '3px 8px',
                                                        borderRadius: 4,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.4px',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {manifest.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 12,
                                                marginTop: 12,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 6,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: 'var(--nj-semantic-color-text-secondary-default)',
                                                    }}
                                                >
                                                    {manifest.views.length} vue
                                                    {manifest.views.length > 1
                                                        ? 's'
                                                        : ''}
                                                </span>
                                                {manifest.tags
                                                    .slice(0, 4)
                                                    .map((tag) => (
                                                        <span
                                                            key={tag}
                                                            style={{
                                                                fontSize: 11,
                                                                color: 'var(--nj-semantic-color-text-secondary-default)',
                                                                background:
                                                                    'var(--nj-core-color-grey-100)',
                                                                padding: '2px 8px',
                                                                borderRadius: 10,
                                                            }}
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                            </div>
                                            {manifest.owner && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: 'var(--nj-semantic-color-text-secondary-default)',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    @{manifest.owner}
                                                </span>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </main>
        </div>
    );
}
